import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  SHAPE_TEXT_INSET_BOTTOM,
  SHAPE_TEXT_INSET_LEFT,
  SHAPE_TEXT_INSET_TOP,
} from '@/lib/text/constants/shape-text-insets'
import { pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import { NATURAL_LEADING_MULTIPLIER } from '@/lib/text/leading'
import type { ShapeTextSegment } from '../types/shape-text-segment'
import type { ShapeTextRow } from '../types/shape-text-row'
import type { ShapeTextFlow } from '../types/shape-text-flow'
import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'
import {
  applyHorizontalInset,
  edgeSweepBands,
  intersectSegments,
  scanlineSegments,
  subtractSegments,
} from './scanline-segments'
import { buildShapePathCommands, flattenShapePath } from './shape-outline-polygon'

/** Smallest row a shape will lay out, in points. Keeps a degenerate or tiny point size from
 *  producing a zero-height row grid; 9pt is the old 12px floor carried across the unit change
 *  (see `canvas-font-metrics.ts` on the pt = CSS px convention). */
export const MIN_ROW_HEIGHT_PT = 9

/**
 * Where to park the caret — and the empty-state placeholder — when `getShapeTextFlow` produces no
 * rows at all: a shape too short for one line band at the current point size
 * (`inset + rowHeight > shape.height`). A brand-new text box dragged near the minimum size is the
 * common case that hits this (WMPCM text-frame-creation caret fix): `clampTextBoxDimensions`'s
 * floor is well under one row's height at the default point size, so the very first thing the user
 * sees can be this fallback, not a real per-row caret.
 *
 * Without the caret half, `layoutShapeTextContent`'s `pushCaret` falls back to shape-local
 * `(0, 0)`: the bounding-box corner, which sits outside the fill of every non-rectangular shape, so
 * the caret appears outside the shape the user is typing into. Without the row half,
 * `packPlaceholderChars` — which iterates `rows` — packs nothing at all and the frame paints no
 * trace whatsoever (WMPCM-9763). Both come from ONE scanline segment, so "the placeholder starts on
 * the row the caret sits on" holds by construction rather than by two functions agreeing.
 *
 * Prefers the shape's own top-left text origin (`SHAPE_TEXT_INSET_TOP`/`_LEFT`) — the same origin
 * a real per-row caret and the "Type here..." placeholder both use — probing a hair below the top
 * edge for a real segment there. Only when the very top has no fill at all (a triangle's apex, a
 * star's notch) does the caret fall through to centring in the fill instead: that heuristic
 * predates the placeholder and existed so an invisible fallback caret would still read as "type
 * here" by sitting in the middle of visible fill — top-left is safe to prefer everywhere else now
 * that the placeholder does that job explicitly. The row keeps its segment's own left edge in both
 * cases, since that is where typing would actually begin.
 *
 * Caret `height` is deliberately a fraction of the shape rather than the full height: the renderer
 * applies a row-band inset (`top: yTop + 2`, `height: max(8, rowHeight - 4)`) that assumes `yTop`
 * is a row top with a full band below it, so a full-height value there draws a near-full-height bar
 * instead of a caret.
 */
export function buildShapeTextFallbackFromPoints(
  points: Array<{ x: number; y: number }>,
  shape: ShapeData,
  rowHeight: number,
  clearancePt = 0
): { caret: ShapeTextCaret; row: ShapeTextRow } {
  const inset = SHAPE_TEXT_INSET_LEFT + clearancePt
  const height = Math.min(rowHeight, Math.max(2, shape.height * 0.6))
  const widestAt = (y: number) =>
    scanlineSegments(points, y).reduce<ShapeTextSegment | null>(
      (best, s) => (best == null || s.endX - s.startX > best.endX - best.startX ? s : best),
      null
    )
  /** Deliberately not `applyHorizontalInset`: that drops any segment narrower than 4pt once inset,
   *  and a frame this small still has to show a character. `packPlaceholderChars`' own "place one
   *  character anyway" rule is what covers an over-narrow row here — this is the empty-frame
   *  placeholder path, and it is deliberately the ONLY place that still paints into a segment too
   *  narrow to hold what it is given. Real text no longer does (see `layoutShapeTextContent`'s
   *  no-clipped-glyph rule, WMPCM-10123); the editor's outline `clipPath` is what keeps this
   *  placeholder character from painting outside the shape. */
  const rowFor = (segment: ShapeTextSegment | null, yTop: number): ShapeTextRow => {
    const startX = (segment?.startX ?? 0) + inset
    const endX = (segment?.endX ?? shape.width) - inset

    return {
      yTop,
      // Mirrors `getShapeTextFlow`'s own baseline formula for a real row.
      baselineY: yTop + rowHeight * 0.8,
      segments: [{ startX, endX: Math.max(startX, endX) }],
      rowHeight,
    }
  }

  const topWidest = widestAt(Math.min(SHAPE_TEXT_INSET_TOP + clearancePt + 0.5, shape.height * 0.5))

  if (topWidest) {
    return {
      caret: { x: topWidest.startX + inset, yTop: 0, rowHeight: height },
      row: rowFor(topWidest, 0),
    }
  }

  // Centre the caret: no fill right at the top (a pointed apex/notch) — a mid-fill caret at least
  // stays inside the outline. scanlineSegments returns [] for a degenerate outline (fewer than 3
  // points) too — fall back to the bbox centre, still a far better guess than the corner.
  const midY = shape.height / 2
  const widest = widestAt(midY)
  const x = widest ? (widest.startX + widest.endX) * 0.5 : shape.width * 0.5
  const yTop = Math.max(0, midY - height * 0.5)

  return { caret: { x, yTop, rowHeight: height }, row: rowFor(widest, yTop) }
}

/**
 * `buildShapeTextFallbackFromPoints` for a caller that has no flattened outline of its own: the
 * WASM boundary, whose Go kernel returns a `fallbackCaret` but knows nothing about `fallbackRow`
 * (see `getShapeTextFlowBoundary`). Only ever called on the no-row path, so the flatten here costs
 * nothing the row loop would not have paid anyway.
 */
export function buildShapeTextFallback(
  shape: ShapeData,
  rowHeight: number
): { caret: ShapeTextCaret; row: ShapeTextRow } {
  return buildShapeTextFallbackFromPoints(
    flattenShapePath(shape),
    shape,
    rowHeight,
    shapeTextEdgeClearancePt(shape)
  )
}

/**
 * Extra clearance, beyond the universal `SHAPE_TEXT_INSET_*`, that text must keep from the outline
 * so it does not run under the shape's own border.
 *
 * `flow.pathD` — the outline the scanline is cast against, and the `clipPath` the editor paints
 * inside — is the stroke's **centerline**. SVG strokes straddle the path they follow, so a visible
 * border occupies `strokeWidth / 2` INSIDE the shape, over ground the scanline reports as free.
 * With only the 1pt `SHAPE_TEXT_INSET_LEFT` to spare, any border heavier than 2pt put glyph ink
 * under its inner half — and on a slanted edge sooner than that, since a horizontal inset buys only
 * `inset x cos(theta)` of perpendicular clearance (a star's arm runs ~52 degrees off vertical, so
 * 1pt horizontal is 0.61pt clear of the edge). That is the "left text is slightly overlapping the
 * border" half of WMPCM-10123.
 *
 * Returns 0 for an unstroked shape, so a borderless frame keeps exactly the capacity it had.
 *
 * Known asymmetry with the renderer, deliberate: `ShapeRenderer` paints
 * `strokeWidth={shape.strokeWidth || 1}`, so a shape carrying a real stroke colour alongside an
 * explicit `strokeWidth: 0` does draw a 1pt hairline that this returns no clearance for. Reading
 * `shape.strokeWidth ?? 1` here instead would inset EVERY borderless shape by 0.5pt, and the load
 * path's `savedStrokeWidth ?? ...` branch (`checkpoint-to-shapes.ts`) makes the zero-weight pairing
 * reachable — silently narrowing a shape whose weight the user set to zero is the worse error, so
 * this errs toward keeping capacity.
 */
export function shapeTextEdgeClearancePt(shape: ShapeData): number {
  const weight = typeof shape.strokeWidth === 'number' ? shape.strokeWidth : 0

  if (!(weight > 0)) return 0
  // `ShapeRenderer` paints `stroke={shape.stroke}` directly, so these are the values that draw
  // nothing. A shape with a real colour and weight 0 is already excluded by the test above.
  if (shape.stroke == null || shape.stroke === 'transparent' || shape.stroke === 'none') return 0

  return weight / 2
}

/**
 * The usable horizontal runs across one row's own vertical band, or `null` when the outline is too
 * narrow anywhere in it.
 *
 * Extracted from the row loop so the loop and `firstUsableRowTop` below cannot drift apart: the
 * anchor has to be chosen by exactly the test that decides whether a row survives, or it would
 * anchor the grid to a band the loop then discards.
 *
 * Two stages, and they answer different questions.
 *
 * **The three probes decide whether the row EXISTS.** Top, middle and bottom of the band,
 * intersected. Their `null` conditions are the whole reason they are still here rather than folded
 * into the sweep below: an empty top or bottom scanline is how a band that runs off the end of the
 * outline gets dropped, so keeping them makes "which bands survive" provably unchanged, including
 * for a caller like `getShapeTextFlowForRowPlan` that can be handed an arbitrary band position.
 *
 * **The edge sweep decides how WIDE it is.** The three probes alone were the "text overflows Star
 * shape" defect (WMPCM-10123 round 4). Intersecting a fixed number of scanlines is only as good as
 * the claim that the band's narrowest point is one of them, and that claim holds for a CONVEX
 * outline — where `x_left` is convex and `x_right` concave in `y`, so both take their extremes at
 * the endpoints — and fails for any outline with a reflex vertex. A star's inner notch sits between
 * the probes: on a 79.7x63.5pt star at 18pt the band reported a left edge of 23.35 where the
 * outline's own is 26.52, `layoutShapeTextContent` placed a glyph into the 3.17pt that does not
 * exist, and the editor's `clipPath` sliced it — QA's "text overlaps the shape's border".
 * `subtractSegments`/`edgeSweepBands` closes it exactly; see `edgeSweepBands` for why no amount of
 * extra sampling is equivalent.
 *
 * Convex kinds (square, circle, triangle, pentagon, hexagon) are unchanged by the sweep as a
 * PROPERTY, not a coincidence: their single segment already starts at the extreme the sweep band
 * ends on, so the subtraction removes nothing.
 *
 * `clearancePt` (see `shapeTextEdgeClearancePt`) is applied on BOTH axes, and it has to be both to
 * work: the probes move `clearancePt` further out than the glyph box so the row is only kept where
 * the outline clears it vertically too, and the horizontal inset grows by the same amount. For a
 * boundary edge `theta` off vertical, an `h` horizontal and `v` vertical margin put the glyph box
 * `h·cos(theta) + v·sin(theta)` from the edge measured perpendicular; at `h = v = clearancePt` that
 * is `clearancePt·(cos+sin) >= clearancePt` for every `theta`. Either axis alone collapses to zero
 * at one of the extremes — which is why widening the horizontal inset on its own would still let
 * text touch a near-horizontal border. At `clearancePt = 0` every term below reduces to what it was.
 */
export function bandSegmentsAt(
  points: Array<{ x: number; y: number }>,
  yTop: number,
  rowHeight: number,
  clearancePt = 0
): ShapeTextSegment[] | null {
  const inset = SHAPE_TEXT_INSET_LEFT + clearancePt
  const midY = yTop + rowHeight * 0.5
  const topY = yTop + 1 - clearancePt
  const botY = yTop + rowHeight - 1 + clearancePt
  const midSegments = applyHorizontalInset(scanlineSegments(points, midY), inset)

  if (midSegments.length === 0) return null
  const topSegments = applyHorizontalInset(scanlineSegments(points, topY), inset)
  const botSegments = applyHorizontalInset(scanlineSegments(points, botY), inset)
  const heightAware = intersectSegments(intersectSegments(midSegments, topSegments), botSegments, 3)

  if (heightAware.length === 0) return null
  // Same `>= 3` floor the intersection above applies: subtracting the swept boundary can leave a
  // sliver either side of a notch, and a slot too narrow to address is worse than no slot.
  const contained = subtractSegments(heightAware, edgeSweepBands(points, topY, botY, inset)).filter(
    (segment) => segment.endX - segment.startX >= 3
  )

  return contained.length === 0 ? null : contained
}

/** Sub-steps of one row height tried when the grid's own top band is unusable. */
export const ANCHOR_SCAN_STEPS = 16

/**
 * Where the row grid should start: the topmost band that is actually usable, rather than the top of
 * the bounding box.
 *
 * The grid used to be pinned to `SHAPE_TEXT_INSET_TOP` and stepped in whole `rowHeight` increments,
 * so a shape that is too narrow at its top (every pointed one — triangle, pentagon, hexagon, star,
 * starburst) had its first band or two discarded with nothing moved up to replace them, and
 * `applyPerLineLeadingToFlow` then pins the first surviving row at its original grid position. The
 * result was a permanent blank row at the apex — WMPCM-10022's "Top gap" column — while the widest
 * part of the outline went unused at the bottom because the grid had already stepped past it.
 *
 * Measured on a 72×72 triangle at 8pt/1.2 (`rowHeight` 9.6): the grid moves 9.6 → 4.2 and gains a
 * fifth row, and every row that holds text sits lower on a widening outline, so it is wider too.
 *
 * A shape whose very first band already works — rectangle, circle, heart — returns `topBound`
 * unchanged on the first test, so this is a no-op for them by construction.
 *
 * Same idea as `closeDroppedRun` in `shape-text-wrap-bands.ts`, which resumes text at an external
 * obstacle's real bottom edge instead of the next whole-row slot.
 */
export function firstUsableRowTop(
  points: Array<{ x: number; y: number }>,
  topBound: number,
  bottomBound: number,
  rowHeight: number,
  clearancePt = 0
): number {
  // The grid's own top band already works — rectangle, circle, heart. Accepted on the SAME test the
  // row loop uses, so those shapes are provably unshifted by this function.
  if (bandSegmentsAt(points, topBound, rowHeight, clearancePt) != null) return topBound

  const step = rowHeight / ANCHOR_SCAN_STEPS

  // Strictly inside one row height: at a full row down we are back to the band the loop would have
  // reached anyway, so there is nothing left to gain and an unusable shape must not shift the grid.
  for (let i = 1; i < ANCHOR_SCAN_STEPS; i++) {
    const yTop = topBound + step * i

    if (yTop + rowHeight > bottomBound + 0.1) break
    if (bandSegmentsAt(points, yTop, rowHeight, clearancePt) != null) return yTop
  }

  return topBound
}

/** Every row the grid produces from `start`, stepping by `rowHeight` to the shape's bottom. */
export function buildRowsFrom(
  points: Array<{ x: number; y: number }>,
  start: number,
  bottomBound: number,
  rowHeight: number,
  clearancePt = 0
): ShapeTextRow[] {
  const rows: ShapeTextRow[] = []

  // Accumulates `yTop += rowHeight` rather than `start + i * rowHeight`: the two diverge in the low
  // bits after a few rows, which would shift row boundaries away from the Go path. The `+ 0.1`
  // epsilon on the bound is load-bearing too.
  for (let yTop = start; yTop + rowHeight <= bottomBound + 0.1; yTop += rowHeight) {
    const segments = bandSegmentsAt(points, yTop, rowHeight, clearancePt)

    if (segments === null) continue
    rows.push({
      yTop,
      // Not the legacy formula (pointSize − descent from the row top) — a real per-run descent
      // isn't available here yet. Tracked separately; see docs/text-unification.md Phase 1 plan
      // and "Two corrections this analysis surfaced" (D4).
      baselineY: yTop + rowHeight * 0.8,
      segments,
      rowHeight,
    })
  }

  return rows
}

/** Total horizontal room a grid offers — the comparison that decides whether anchoring helped. */
export function usableWidthOf(rows: ShapeTextRow[]): number {
  let total = 0

  for (const row of rows) {
    for (const segment of row.segments) total += segment.endX - segment.startX
  }

  return total
}

/**
 * Caps the frame-wide row pitch so at least one row always exists to hold text.
 *
 * The row grid is spaced by the LEADING (`resolveRowGridBasis` raises the pitch to the leading, never
 * lowers it). A leading taller than the frame's own text area therefore left the grid with zero rows:
 * nothing was laid out, so dragging the Leading slider upward made the whole story vanish from the
 * frame while the model still held every character (WMPCM-11367). Real leading is applied per row
 * afterwards by `applyPerLineLeadingToFlow`, which never drops the first row, so a pitch cap here only
 * decides how many rows the grid offers, not where the first one sits.
 *
 * Never capped below one natural line (`NATURAL_LEADING_MULTIPLIER`): a frame too short for even that
 * is genuinely overset, and the honest answer there is still "no row". Returns the multiplier
 * unchanged whenever the pitch already fits, so every other frame lays out exactly as before.
 */
export function clampLeadingMultToFrame(
  shape: ShapeData,
  fontSizePt: number,
  leadingMult: number
): number {
  if (!(fontSizePt > 0)) return leadingMult

  const clearancePt = shapeTextEdgeClearancePt(shape)
  const availablePt =
    shape.height - SHAPE_TEXT_INSET_BOTTOM - SHAPE_TEXT_INSET_TOP - 2 * clearancePt
  const pitchPt = Math.max(MIN_ROW_HEIGHT_PT, fontSizePt * leadingMult)

  if (pitchPt <= availablePt) return leadingMult

  const cappedPt = Math.max(availablePt, fontSizePt * NATURAL_LEADING_MULTIPLIER)

  return Math.min(leadingMult, cappedPt / fontSizePt)
}

export function getShapeTextFlow(
  shape: ShapeData,
  // Points, like the shape geometry it is flowed against — see `canvas-font-metrics.ts` on why the
  // document canvas has no 96/72 in it.
  fontSizePt: number,
  // Match the text tool's natural line height by default so shape text and text-tool text lay out
  // identically (WMPCM-8162); callers override with a resolved multiplier from stored leading.
  requestedLeadingMult = NATURAL_LEADING_MULTIPLIER
): ShapeTextFlow {
  const leadingMult = clampLeadingMultToFrame(shape, fontSizePt, requestedLeadingMult)
  const commands = buildShapePathCommands(shape)
  const points = flattenShapePath(shape)
  const pathD = shape.type === 'path' && shape.path ? shape.path : pathCommandsToSvgPath(commands)
  const rowHeight = Math.max(MIN_ROW_HEIGHT_PT, fontSizePt * leadingMult)
  // Universal inset (SHAPE_TEXT_INSET_*, text-unification Phase 1 D4) — a rectangle's scanline is
  // its own full width, so this single rule replaces the old per-shape-type table with no special
  // case for the rectangle. See docs/text-unification.md Appendix B for the NSD derivation.
  // The stroke's inner half is not free ground — see `shapeTextEdgeClearancePt`. It comes off the
  // vertical bounds here and off both axes inside `bandSegmentsAt`.
  const clearancePt = shapeTextEdgeClearancePt(shape)
  const topBound = SHAPE_TEXT_INSET_TOP + clearancePt
  const bottomBound = Math.max(topBound, shape.height - SHAPE_TEXT_INSET_BOTTOM - clearancePt)
  const anchor = firstUsableRowTop(points, topBound, bottomBound, rowHeight, clearancePt)
  let rows = buildRowsFrom(points, topBound, bottomBound, rowHeight, clearancePt)

  // Anchoring is an OFFER, not a rule. Sliding the grid down to the first usable band buys a lot on
  // some outlines (a 72×72 triangle at 8pt gains a row and 55% more characters) and costs on others:
  // capacity is not monotonic in the anchor, because moving the grid also moves every row below it,
  // and a shape can lose more at its waist than it gains at its tip. A five-point star measured 16
  // characters un-anchored and 10 under one plausible anchoring rule.
  //
  // So build both grids and keep the wider one. That makes "never worse than before" a property of
  // the code rather than of the heuristic, and it costs a second row scan only on shapes whose top
  // band is unusable — a rectangle, circle or heart returns `topBound` above and never gets here.
  if (anchor !== topBound) {
    const anchored = buildRowsFrom(points, anchor, bottomBound, rowHeight, clearancePt)

    if (usableWidthOf(anchored) > usableWidthOf(rows)) rows = anchored
  }

  const fallback = buildShapeTextFallbackFromPoints(points, shape, rowHeight, clearancePt)

  return {
    rows,
    rowHeight,
    bottomBound,
    pathD,
    fallbackCaret: fallback.caret,
    fallbackRow: fallback.row,
  }
}

/**
 * The same row engine, but for a caller that already knows where each row belongs and how tall it
 * is — `layoutShapeTextWithPerLineLeading`'s second pass, which re-probes the outline at the
 * positions the per-line-leading re-stack actually produced.
 *
 * Only the bands change; `pathD`, `rowHeight` and the empty-frame fallbacks are carried over from
 * the flow the plan was derived from, so the clip path and the placeholder anchor stay put.
 */
export function getShapeTextFlowForRowPlan(
  shape: ShapeData,
  plan: Array<{ yTop: number; rowHeight: number }>,
  base: ShapeTextFlow
): ShapeTextFlow {
  const points = flattenShapePath(shape)
  // Must match `getShapeTextFlow`'s own clearance: this re-probes the outline at the positions the
  // per-line-leading re-stack produced, and a pass that forgot the border would hand back wider
  // segments than the grid it is replacing.
  const clearancePt = shapeTextEdgeClearancePt(shape)
  const rows: ShapeTextRow[] = []

  for (const entry of plan) {
    const segments = bandSegmentsAt(points, entry.yTop, entry.rowHeight, clearancePt)

    if (segments === null) continue
    rows.push({
      yTop: entry.yTop,
      baselineY: entry.yTop + entry.rowHeight * 0.8,
      segments,
      rowHeight: entry.rowHeight,
    })
  }

  return { ...base, rows }
}
